import React, { useRef, useState } from "react";

import { useHash } from "../hooks/use-hash";
import { useAnchorScrollListener } from "../hooks/use-anchor-scroll-listener";
import { useInitialHashScroll } from "../hooks/use-initial-hash-scroll";
import { getElementScrollPosition } from "../utils/get-element-scroll-position";
import { getHashTargetId } from "../utils/get-hash-target-id";
import { AnchorContext, TContext } from "./anchor-context";

export interface TStore {
  sections: TContext["sections"];
  scroller: HTMLElement | null;
}

interface TProps {
  children: React.ReactNode[];
  getScroller?: () => TStore["scroller"];
  offset?: number;
}

export function AnchorProvider({ children, getScroller, offset = 0 }: TProps) {
  const ref = useRef<TStore>({
    sections: [],
    scroller: null
  });
  // When opened from a link with a hash, don't let the scroll listener
  // overwrite it before useInitialHashScroll brings the section into view
  const [blockScrollEvent, setBlockScrollEvent] = useState(() => !!getHashTargetId());

  if (getScroller) {
    ref.current.scroller = getScroller();
  }

  const [hash, setHash] = useHash(ref, setBlockScrollEvent);
  useAnchorScrollListener(ref, setHash, offset, blockScrollEvent);
  useInitialHashScroll(ref, offset, setBlockScrollEvent);

  // Called when a <AnchorSection /> is mounted
  // It will be called with the current HTMLElement to be stored in
  // a list. The list should be sorted by element scroll position to
  // optimize the onscroll event.
  const registerSection: TContext["registerSection"] = element => {
    const { sections, scroller } = ref.current;

    if (sections.includes(element)) {
      return;
    }

    const y = getElementScrollPosition(element, scroller);
    const index = sections.findIndex(
      section => getElementScrollPosition(section, scroller) > y
    );

    if (index === -1) {
      sections.push(element);
    } else {
      sections.splice(index, 0, element);
    }
  };

  // Called when an <AnchorSection is unmounted
  // It will be called with the current element so we can delete it from
  // the list
  const unregisterSection: TContext["unregisterSection"] = element => {
    const index = ref.current.sections.indexOf(element);
    if (index !== -1) {
      ref.current.sections.splice(index, 1);
    }
  };

  return (
    <AnchorContext.Provider
      value={{
        offset,
        hash,
        sections: ref.current.sections,
        setHash,
        registerSection,
        unregisterSection
      }}
    >
      {children}
    </AnchorContext.Provider>
  );
}
